import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  LayoutGrid,
  Link2,
  Maximize2,
  Minus,
  MousePointerClick,
  RotateCcw,
  Settings2,
  X,
} from 'lucide-react';
import { analyticsApi, type AnalyticsApi, type AnalyticsRange } from '../../api/analytics';
import type { AnalyticsSite, AnalyticsTop, DashboardCard } from '../../api/types';
import { useLoad } from '../../lib/useLoad';
import { useCapabilities } from '../../lib/capabilities';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { RefreshButton } from '../../components/RefreshButton';
import { Spinner } from '../../components/Spinner';
import { useTimeFormat } from '../../lib/tenantTime';
import type { SettingsTarget } from '../../embed/bridge';
import { BarList, Donut, LineChart, Matrix, Sparkline } from './charts/Charts';
import { DONUT_SLOTS, OTHER, seriesColor } from './charts/palette';
import {
  DOW_LABELS,
  type Delta,
  channelLabel,
  delta,
  duration,
  exact,
  flagOf,
  money,
  percent,
  shortDay,
  toneOf,
} from './analyticsFormat';
import {
  CARD_CATALOGUE,
  DEFAULT_LAYOUT,
  cycleWidth,
  groupCards,
  moveCard,
  reconcile,
  specOf,
  toggleHidden,
} from './layout';
// ~80 kB of country outlines, split out so it is fetched only by someone who
// actually looks at the map.
const WorldMap = lazy(() => import('./WorldMap').then((m) => ({ default: m.WorldMap })));

import { CardDetail } from './CardDetail';
import { canExpand, type DetailTable } from './detail';
import { HeatmapView } from './HeatmapView';
import { InstallManage, NewInstall } from './InstallManage';
import type { InstallSection, ManageLink } from './installForm';
import { UtmBuilder } from './UtmBuilder';
import { downloadCsv } from './exports';
import { SessionDrawer } from './SessionDrawer';
import './analytics.css';

/** The ranges an operator actually asks for, in the tenant's own calendar. */
const RANGES = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '28d', label: '28 days', days: 28 },
  { key: '90d', label: '90 days', days: 90 },
] as const;

/**
 * What each tabbed card can be switched between.
 *
 * Seven cards used to draw the same ranked list of seven different dimensions.
 * They are now three cards carrying their dimensions as tabs, which is both
 * fewer things to arrange and — more to the point — the only arrangement in
 * which two answers to the same question sit in one place to be compared.
 *
 * `webOnly` is per TAB rather than per card now. Hiding a whole card was right
 * while the card WAS the referrer list; an app install still has the question
 * "where did they come from", it just has no referrer to answer it with.
 */
const ACQ_TABS = [
  { key: 'channels', label: 'Channels', webOnly: false },
  { key: 'referrers', label: 'Referrers', webOnly: true },
  { key: 'campaigns', label: 'Campaigns', webOnly: false },
] as const;
const CONTENT_TABS = [
  { key: 'pages', label: 'Top pages', webOnly: false },
  { key: 'entry', label: 'Landing', webOnly: true },
] as const;
const DEVICE_TABS = [
  { key: 'devices', label: 'Devices' },
  { key: 'browsers', label: 'Browsers' },
] as const;

export type AcqTab = (typeof ACQ_TABS)[number]['key'];
type ContentTab = (typeof CONTENT_TABS)[number]['key'];
type DeviceTab = (typeof DEVICE_TABS)[number]['key'];

/**
 * A card's tab strip.
 *
 * Deliberately lighter than `.an-seg`: the range and the comparison govern the
 * whole screen, a tab governs one card, and drawing them at the same weight
 * would make a card look like it carried its own filters.
 */
function TabStrip({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: readonly { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
  label: string;
}) {
  // One tab is not a choice — an app install whose only web-only sibling was
  // dropped gets the list with no chrome over it.
  if (tabs.length < 2) return null;
  return (
    <div className="an-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={value === tab.key}
          className={value === tab.key ? 'on' : ''}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

const civilDaysAgo = (days: number): string => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};

export function AnalyticsView({
  api = analyticsApi,
  embedded = false,
  switcher = null,
  manageLink = null,
  onManageLinkUsed,
  onOpenSettings = null,
  settingsAllowed = true,
  initialAcqTab,
  onOpenCampaign,
}: {
  /** The CommsSource seam — the embed and the preview route inject theirs. */
  api?: AnalyticsApi;
  embedded?: boolean;
  /**
   * The Dashboard section's Operations/Analytics switch, drawn at the start of
   * the heading row. Absent in the Communications module, which has its own tabs.
   */
  switcher?: React.ReactNode;
  /**
   * Open Install and settings on this install — the dispatch dashboard's
   * Manage in CRM, read off the standalone route's address (manageLinkOf).
   * Honoured once, when the install list first answers, and only for an
   * install on it; anything else lands on the dashboard as if there were no
   * link. `onManageLinkUsed` is told either way, so the route can drop it from
   * the address and a reload does not re-open what the reader closed.
   */
  manageLink?: ManageLink | null;
  onManageLinkUsed?: () => void;
  /**
   * Install and settings live somewhere else — the dispatch dashboard's My
   * Website → Analytics, which frames them as a page of their own. Given, every
   * way into the settings on this screen goes there instead of opening them
   * in place, so the reports and the settings each have exactly one home.
   */
  onOpenSettings?: ((target: SettingsTarget) => void) | null;
  /**
   * False when the framing host keeps Install and settings from this person
   * (`HostAnalytics.settings`): the screen is the reports, with no way into
   * the settings at all — not here, not at the host. Not a CRM grant; the
   * agent's `analytics` feature still applies when it is true.
   */
  settingsAllowed?: boolean;
  /**
   * Which tab the acquisition card opens on — `campaigns` when a campaign's
   * Results sent the reader here to compare it with the others.
   */
  initialAcqTab?: AcqTab;
  /**
   * Open one CRM campaign, from a Campaigns row its tracked links produced
   * (`campaign` on the row). Absent where the mount cannot reach Communications
   * — the framed dashboard and My Website pages — and the rows then stay plain.
   */
  onOpenCampaign?: (id: number) => void;
}) {
  const { me } = useCapabilities();
  const { fmtDateTime, fmtTime } = useTimeFormat();
  const level = me?.access.features.analytics;
  // Deliberately permissive when capabilities have not loaded — the same
  // posture as lib/capabilities.ts, so a slow /meta does not flash a refusal.
  const mayManage = level === undefined || level === 'manage';

  const [rangeKey, setRangeKey] = useState<(typeof RANGES)[number]['key']>('28d');
  const [compare, setCompare] = useState<'previous' | 'year' | 'none'>('previous');
  const [siteId, setSiteId] = useState<number | undefined>();
  const [editing, setEditing] = useState(false);
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [openCard, setOpenCard] = useState<string | null>(null);
  // Which part of Install and settings to open on, or null when it is closed.
  // 'auto' leaves the choice to the screen; the entry points that know what
  // the reader came for (the never-reported note, the heatmap's off switch)
  // name it instead of landing them on the snippet.
  const [managing, setManaging] = useState<InstallSection | 'auto' | null>(null);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  // Which dimension each tabbed card is showing. Card-local and deliberately
  // NOT saved with the layout: an arrangement is something an agent keeps, a
  // tab is where they happen to be looking this minute.
  const [acqTab, setAcqTab] = useState<AcqTab>(initialAcqTab ?? 'channels');
  const [contentTab, setContentTab] = useState<ContentTab>('pages');
  const [deviceTab, setDeviceTab] = useState<DeviceTab>('devices');
  const [toolsOpen, setToolsOpen] = useState(false);

  /** Into Install and settings: in place, or wherever the host keeps them. */
  const openSettings = (section: InstallSection | 'auto', siteForLink: number | null) => {
    if (!settingsAllowed) return;
    if (onOpenSettings) onOpenSettings({ siteId: siteForLink, section: section === 'auto' ? null : section });
    else setManaging(section);
  };

  const sites = useLoad(() => api.listAnalyticsSites(), []);
  const site: AnalyticsSite | undefined = useMemo(() => {
    const all = sites.data ?? [];
    return all.find((row) => row.id === siteId) ?? all[0];
  }, [sites.data, siteId]);

  // A link from outside, spent on the first answer of the install list.
  const linkSpent = useRef(false);
  useEffect(() => {
    if (!manageLink || linkSpent.current || !sites.data) return;
    linkSpent.current = true;
    if (settingsAllowed && sites.data.some((row) => row.id === manageLink.siteId)) {
      // Acting on the list's arrival IS the point: the link names an install
      // the screen cannot know exists until the list says so.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSiteId(manageLink.siteId);
      setManaging(manageLink.section);
    }
    onManageLinkUsed?.();
  }, [manageLink, sites.data, onManageLinkUsed, settingsAllowed]);

  const range: AnalyticsRange = useMemo(() => {
    const days = RANGES.find((entry) => entry.key === rangeKey)?.days ?? 28;
    return {
      siteId: site?.id,
      from: civilDaysAgo(days - 1),
      to: civilDaysAgo(0),
      compare: compare === 'none' ? undefined : compare,
    };
  }, [rangeKey, compare, site?.id]);

  const ready = Boolean(site);
  const overview = useLoad(() => (ready ? api.getAnalyticsOverview(range) : Promise.resolve(null)), [range, ready]);
  const series = useLoad(() => (ready ? api.getAnalyticsSeries(range, 'sessions') : Promise.resolve([])), [range, ready]);
  /*
   * The band's sparklines.
   *
   * Only for the four metrics `/analytics/timeseries` actually answers: engaged
   * time and bounce rate have no daily series, so those two tiles carry no
   * sparkline at all rather than a sessions-shaped line drawn under a different
   * measure. Asked WITHOUT a comparison — a sparkline draws one trace, and the
   * compare leg is a second query per metric server-side.
   */
  const sparkRange = useMemo(() => ({ ...range, compare: undefined }), [range]);
  const visitorSeries = useLoad(
    () => (ready ? api.getAnalyticsSeries(sparkRange, 'visitors') : Promise.resolve([])),
    [sparkRange, ready],
  );
  const pageviewSeries = useLoad(
    () => (ready ? api.getAnalyticsSeries(sparkRange, 'pageviews') : Promise.resolve([])),
    [sparkRange, ready],
  );
  const bookingSeries = useLoad(
    () => (ready ? api.getAnalyticsSeries(sparkRange, 'bookings') : Promise.resolve([])),
    [sparkRange, ready],
  );
  const channels = useLoad(() => (ready ? api.getAnalyticsTop(range, 'channel', 8) : Promise.resolve(null)), [range, ready]);
  const referrers = useLoad(() => (ready ? api.getAnalyticsTop(range, 'referrer', 8) : Promise.resolve(null)), [range, ready]);
  const campaigns = useLoad(() => (ready ? api.getAnalyticsTop(range, 'utm_campaign', 8) : Promise.resolve(null)), [range, ready]);
  const pages = useLoad(() => (ready ? api.getAnalyticsTop(range, 'page', 8) : Promise.resolve(null)), [range, ready]);
  const entries = useLoad(() => (ready ? api.getAnalyticsTop(range, 'entry_page', 8) : Promise.resolve(null)), [range, ready]);
  const countries = useLoad(() => (ready ? api.getAnalyticsTop(range, 'country', 40) : Promise.resolve(null)), [range, ready]);
  const [geoCountry, setGeoCountry] = useState<string | null>(null);
  const geoDrill = useLoad(
    () =>
      ready && geoCountry
        ? api.getAnalyticsTop(range, 'city', 10, geoCountry)
        : Promise.resolve(null),
    [range, ready, geoCountry],
  );
  const devices = useLoad(() => (ready ? api.getAnalyticsTop(range, 'device', 6) : Promise.resolve(null)), [range, ready]);
  const browsers = useLoad(() => (ready ? api.getAnalyticsTop(range, 'browser', 5) : Promise.resolve(null)), [range, ready]);
  const traffic = useLoad(() => (ready ? api.getAnalyticsTrafficTable(range) : Promise.resolve(null)), [range, ready]);
  const funnel = useLoad(() => (ready ? api.getAnalyticsFunnel(range) : Promise.resolve([])), [range, ready]);
  const sessions = useLoad(
    () => (ready ? api.listAnalyticsSessions(range, { page: 1 }) : Promise.resolve(null)),
    [range, ready],
  );
  const notes = useLoad(() => (ready ? api.listAnalyticsAnnotations(range) : Promise.resolve([])), [range, ready]);
  const layoutLoad = useLoad(() => (ready ? api.getAnalyticsDashboard(site?.id) : Promise.resolve(null)), [ready, site?.id]);

  // The arrangement is DERIVED from the server's copy until the agent moves
  // something, at which point `edited` takes over. Seeding it from an effect
  // instead would render the default layout for one frame on every load — cards
  // visibly jumping into place — and would clobber a drag that landed while the
  // request was still in flight.
  const [edited, setEdited] = useState<DashboardCard[] | null>(null);
  const cards = edited ?? reconcile(layoutLoad.data?.cards);

  // ---- the live gauge -----------------------------------------------------
  // Polled rather than pushed: a live count is a gauge, not a log, and the
  // server caches it for five seconds so twenty agents cost one Redis read.
  // Backed off to a minute after three empty polls, because most installs have
  // nobody on them most of the time.
  const [live, setLive] = useState<{ active: number; byPath: { path: string; active: number }[]; known: { name: string | null }[] } | null>(null);
  const [liveNonce, setLiveNonce] = useState(0);
  const emptyPolls = useRef(0);
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    let timer: number | undefined;
    const tick = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const answer = await api.getAnalyticsLive(site?.id);
          if (cancelled) return;
          setLive({ active: answer.stats.active, byPath: answer.payload, known: answer.known ?? [] });
          emptyPolls.current = answer.stats.active === 0 ? emptyPolls.current + 1 : 0;
        } catch {
          // A live number that cannot be fetched is not worth an error banner.
        }
      }
      timer = window.setTimeout(tick, emptyPolls.current >= 3 ? 60000 : 20000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // `liveNonce` restarts the poller, whose first tick is immediate — which is
    // what makes Refresh reach this card too.
  }, [ready, site?.id, api, liveNonce]);

  /*
   * One control refetches the whole screen.
   *
   * Every card is a separate query and none of them polls, so a dashboard left
   * open all morning is showing the morning's numbers with nothing on it to say
   * so — and the only way to a fresh figure was reloading the browser, which
   * also throws away the range, the drill-down and the scroll position. The
   * layout is deliberately NOT in this list: it is the reader's own arrangement,
   * not data, and re-reading it mid-edit would fight `edited`.
   */
  const dataLoads = [
    sites,
    overview,
    series,
    visitorSeries,
    pageviewSeries,
    bookingSeries,
    channels,
    referrers,
    campaigns,
    pages,
    entries,
    countries,
    geoDrill,
    devices,
    browsers,
    traffic,
    funnel,
    sessions,
    notes,
  ];
  const refreshing = dataLoads.some((load) => load.refreshing);
  const refreshAll = () => {
    for (const load of dataLoads) load.reload();
    // The gauge is on its own timer rather than in `useLoad`, so it is bumped
    // by hand — otherwise Refresh moves every figure on the screen except the
    // one that says "now".
    setLiveNonce((n) => n + 1);
  };

  /*
   * When these numbers were read.
   *
   * Refresh has always been here; what was missing was any way to tell whether
   * it was needed — a dashboard left open all morning shows the morning's
   * figures, and nothing on the screen said so.
   *
   * Derived rather than stored: setting state from an effect on `overview.data`
   * would re-render the whole screen a second time on every load to move one
   * clock label. The memo re-runs exactly when a fetch lands, which is the
   * moment being stamped.
   */
  const lastUpdated = useMemo(
    () => (overview.data ? new Date().toISOString() : null),
    [overview.data],
  );

  // The tools menu closes on a click anywhere else and on Escape. Mousedown
  // rather than click, so a press that starts outside cannot land on whatever
  // the menu was covering.
  useEffect(() => {
    if (!toolsOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!toolsRef.current?.contains(event.target as Node)) setToolsOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setToolsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  // ---- the layout editor --------------------------------------------------
  // The card being dragged, by id rather than by index: the grid is rendered in
  // section groups now, so a card's position in the flat array is no longer the
  // position it was dropped at.
  const dragId = useRef<string | null>(null);
  const toolsRef = useRef<HTMLDivElement | null>(null);
  const persist = useCallback(
    (next: DashboardCard[]) => {
      setEdited(next);
      if (site) void api.saveAnalyticsDashboard(site.id, next).catch(() => {});
    },
    [site, api],
  );

  const groups = groupCards(cards);
  const hiddenCards = cards.filter((card) => card.hidden);

  // `off` is a real grant, not an absence: the server refuses every call below
  // and would leave fifteen cards each rendering their own 1036. Said once here
  // in the copy middlewares/auth.js uses, it reads as a decision rather than a
  // fault. `undefined` is "/meta has not landed" and stays permissive.
  if (level === 'off') {
    return (
      <div className="analytics">
        <div className="an-head">
          <div className="an-head-id">
            {switcher}
            <h1 className="an-title">Website analytics</h1>
          </div>
        </div>
        <Note tone="warn">
          Your role does not include website analytics. An administrator grants it under Settings →
          Users &amp; access.
        </Note>
      </div>
    );
  }
  if (sites.loading && !sites.data) {
    return (
      <div className={embedded ? 'analytics embedded' : 'analytics'}>
        {switcher ? (
          <div className="an-head">
            <div className="an-head-id">{switcher}</div>
          </div>
        ) : null}
        <Spinner />
      </div>
    );
  }
  if (sites.error) {
    return (
      <div className={embedded ? 'analytics embedded' : 'analytics'}>
        {switcher ? (
          <div className="an-head">
            <div className="an-head-id">{switcher}</div>
          </div>
        ) : null}
        <ErrorNote error={sites.error} />
      </div>
    );
  }
  if (!site) {
    // `embedded` matters here as much as anywhere: inside the dashboard this is
    // the module's scroller, and without the class the first-run screen had no
    // scroll of its own and the gutter of a standalone page.
    return (
      <div className={embedded ? 'analytics embedded' : 'analytics'}>
        {/* The first-run screen is where "Set up analytics" lands, so the way
            back to Operations has to be on it. */}
        {switcher ? (
          <div className="an-head">
            <div className="an-head-id">{switcher}</div>
          </div>
        ) : null}
        {mayManage && settingsAllowed && onOpenSettings ? (
          <Note tone="info">
            Website analytics is set up under <b>My Website → Analytics</b>, and its reports appear here.{' '}
            <button type="button" className="an-link" onClick={() => openSettings('auto', null)}>
              Set it up
            </button>
          </Note>
        ) : mayManage && settingsAllowed ? (
          <NewInstall
            api={api}
            onCreated={(id) => {
              setSiteId(id);
              sites.reload();
              setManaging('install');
            }}
          />
        ) : (
          <>
            {switcher ? null : (
              <div className="an-head">
                <div>
                  <h1 className="an-title">Website analytics</h1>
                  <p className="an-sub">Who visits your site, what they look at, and whether they book.</p>
                </div>
              </div>
            )}
            <Note tone="info">No install has been set up yet. An administrator can add one.</Note>
          </>
        )}
      </div>
    );
  }

  if (heatmapOpen) {
    return (
      <div className={embedded ? 'analytics embedded' : 'analytics'}>
        <HeatmapView
          api={api}
          site={site}
          range={range}
          onClose={() => setHeatmapOpen(false)}
          onOpenSettings={
            settingsAllowed
              ? (section) => {
                  setHeatmapOpen(false);
                  openSettings(section, site.id);
                }
              : undefined
          }
        />
      </div>
    );
  }

  if (managing) {
    return (
      <div className={embedded ? 'analytics embedded' : 'analytics'}>
        {/* Keyed on the install: switching to another one starts its forms from
            ITS values, not from the edits made to the last one. */}
        <InstallManage
          key={site.id}
          api={api}
          site={site}
          sites={sites.data ?? []}
          mayManage={mayManage}
          initialSection={managing === 'auto' ? undefined : managing}
          refreshing={sites.refreshing}
          onChanged={sites.reload}
          onSelect={(id) => setSiteId(id || undefined)}
          onCreated={(id) => {
            setSiteId(id);
            sites.reload();
            setManaging('install');
          }}
          onClose={() => setManaging(null)}
          onRemoved={() => setManaging(null)}
        />
      </div>
    );
  }

  const stats = overview.data?.stats;
  const previous = overview.data?.previous ?? null;
  const busy = overview.stale || series.stale;

  /**
   * The change against the comparison period, or its absence.
   *
   * `compare: 'none'` is a real answer rather than a missing one: without this
   * every tile showed `delta(x, undefined)`'s em dash beside the words "vs
   * previous", naming a comparison the reader had just switched off.
   */
  const changeOf = (current: number, before: number | undefined): Delta =>
    compare === 'none'
      ? { ratio: null, label: 'not compared', direction: 'none' }
      : delta(current, before ?? null);

  /**
   * What the comparison is against, in the filter bar's own words.
   *
   * It used to read "vs previous" unconditionally — so every figure on the
   * screen mislabelled itself the moment an agent switched to last year.
   */
  const compareFoot = compare === 'year' ? 'vs last year' : 'vs previous';

  // The arrow carries the direction a second time, without colour: a red and a
  // green chip at 11px are the same chip to a reader who cannot separate the
  // two, and these rows are nothing but small coloured type.
  const arrowFor = (direction: Delta['direction']) =>
    direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus;

  const statTile = (
    label: string,
    value: string,
    current: number,
    before: number | undefined,
    higherIsBetter = true,
  ) => {
    const change = changeOf(current, before);
    const Arrow = arrowFor(change.direction);
    return (
      <div className="an-stat" key={label}>
        <div className="an-stat-label">{label}</div>
        <div className="an-stat-value">{value}</div>
        <div className="an-stat-foot">
          <span className={`an-delta ${toneOf(change.direction, higherIsBetter)}`}>
            <Arrow size={11} strokeWidth={2.5} />
            {change.label}
          </span>
          {compare === 'none' ? null : <span>{compareFoot}</span>}
        </div>
      </div>
    );
  };

  /**
   * One figure in the headline band.
   *
   * On the page ground rather than inside a card, at a size nothing below it
   * reaches: these six are what every card underneath is read AGAINST, so the
   * hierarchy has to say so before the reader starts comparing bars. They also
   * carry the exact figure rather than `compact()`'s abbreviation — that was a
   * response to six tiles sharing a third of the grid, and they no longer do.
   */
  const bandTile = (
    label: string,
    value: string,
    current: number,
    before: number | undefined,
    spark: number[],
    higherIsBetter = true,
  ) => {
    const change = changeOf(current, before);
    const Arrow = arrowFor(change.direction);
    return (
      <div className="an-band-tile" key={label}>
        <div className="an-band-label">{label}</div>
        <div className="an-band-value">{value}</div>
        <div className="an-band-foot">
          <span className={`an-delta ${toneOf(change.direction, higherIsBetter)}`}>
            <Arrow size={11} strokeWidth={2.5} />
            {change.label}
          </span>
          {spark.length > 1 ? <Sparkline values={spark} /> : null}
        </div>
      </div>
    );
  };

  /**
   * What each card hands to a spreadsheet.
   *
   * Deliberately the SAME numbers the card shows rather than a fresh
   * unfiltered query — an export that quietly disagrees with the screen it came
   * from is worse than no export.
   */
  const dimCsv = (name: string, data: AnalyticsTop | null) => () =>
    downloadCsv(
      `${site?.name ?? 'analytics'}-${name}-${range.from}-to-${range.to}.csv`,
      ['value', 'sessions', 'share', 'bounce_rate', 'avg_engaged_seconds', 'bookings', 'booking_value'],
      (data?.payload ?? []).map((row) => [
        row.value,
        row.sessions,
        row.share,
        row.bounceRate,
        row.avgEngagedSeconds,
        row.bookings,
        row.bookingValue,
      ]),
    );

  const EXPORTS: Record<string, (() => void) | undefined> = {
    // A tabbed card exports the tab that is ON SCREEN, not all three of its
    // dimensions — the same rule that makes these the card's own numbers rather
    // than a fresh unfiltered query: an export that quietly disagrees with the
    // card it came from is worse than no export.
    acquisition:
      acqTab === 'channels'
        ? dimCsv('channels', channels.data)
        : acqTab === 'referrers'
          ? dimCsv('referrers', referrers.data)
          : dimCsv('campaigns', campaigns.data),
    content: contentTab === 'pages' ? dimCsv('pages', pages.data) : dimCsv('landing-pages', entries.data),
    geo: dimCsv('countries', countries.data),
    devices: deviceTab === 'devices' ? dimCsv('devices', devices.data) : dimCsv('browsers', browsers.data),
    bookings: () =>
      downloadCsv(
        `${site?.name ?? 'analytics'}-funnel-${range.from}-to-${range.to}.csv`,
        ['step', 'sessions', 'share', 'drop_off'],
        (funnel.data ?? []).map((step) => [step.label, step.sessions, step.share, step.dropOff]),
      ),
    trend: () =>
      downloadCsv(
        `${site?.name ?? 'analytics'}-daily-${range.from}-to-${range.to}.csv`,
        ['day', 'sessions', 'booking_value', 'previous_period_sessions'],
        (series.data ?? []).map((point) => [point.day, point.value, point.bookingValue, point.previous]),
      ),
    sessions: () =>
      downloadCsv(
        `${site?.name ?? 'analytics'}-visits-${range.from}-to-${range.to}.csv`,
        ['started_at', 'who', 'channel', 'entry_path', 'exit_path', 'pageviews', 'engaged_seconds', 'country', 'city', 'outcome', 'booking_value'],
        (sessions.data?.payload ?? []).map((row) => [
          row.startedAt,
          row.contactName ?? (row.isIdentified ? 'signed in' : 'anonymous'),
          row.channel,
          row.entryPath,
          row.exitPath,
          row.pageviews,
          row.engagedSeconds,
          row.country,
          row.city,
          row.outcome,
          row.bookingValue,
        ]),
      ),
    funnel: () =>
      downloadCsv(
        `${site?.name ?? 'analytics'}-funnel-${range.from}-to-${range.to}.csv`,
        ['step', 'sessions', 'share_of_first_step', 'dropped_from_previous'],
        (funnel.data ?? []).map((step) => [step.label, step.sessions, step.share, step.dropOff]),
      ),
    hours: () =>
      downloadCsv(
        `${site?.name ?? 'analytics'}-when-they-visit-${range.from}-to-${range.to}.csv`,
        ['day_of_week', 'hour', 'sessions'],
        (traffic.data?.payload ?? []).map((cell) => [DOW_LABELS[cell.dow], cell.hour, cell.sessions]),
      ),
  };

  const card = (id: string, body: React.ReactNode, tabs?: React.ReactNode) => {
    const spec = specOf(id);
    const entry = cards.find((row) => row.id === id);
    if (!spec || !entry) return null;
    return (
      <section
        key={id}
        // Named in the DOM so the handbook's capture script can photograph one
        // card rather than the whole screen; there is no styling on it.
        data-card={id}
        className={`an-card${editing ? ' editing' : ''}`}
        style={{ ['--span' as string]: entry.w }}
        draggable={editing}
        onDragStart={() => {
          dragId.current = id;
        }}
        onDragOver={(event) => editing && event.preventDefault()}
        onDrop={() => {
          if (!editing || !dragId.current) return;
          // `moveCard` refuses a drop into another band rather than reordering
          // across one — see its comment for why the headings are worth that
          // one freedom.
          persist(moveCard(cards, dragId.current, id));
          dragId.current = null;
        }}
      >
        <div className="an-card-head">
          <h2 className={`an-card-title${editing ? ' an-drag-handle' : ''}`}>{spec.title}</h2>
          {editing ? (
            <div className="an-card-tools">
              <button type="button" onClick={() => persist(cycleWidth(cards, id))} title="Change width">
                {entry.w}/3
              </button>
              <button type="button" onClick={() => persist(toggleHidden(cards, id))} title="Hide this card">
                <X size={12} />
              </button>
            </div>
          ) : (
            <span className="an-card-head-end">
              {tabs}
              {EXPORTS[id] ? (
                <button
                  type="button"
                  className="an-card-export"
                  title={`Download ${spec.title.toLowerCase()} as a spreadsheet`}
                  aria-label={`Download ${spec.title.toLowerCase()} as a spreadsheet`}
                  onClick={() => EXPORTS[id]?.()}
                >
                  <Download size={13} />
                </button>
              ) : null}
              {/* Absent, not disabled, on a card that has nothing further to
                  say — see CardDetail's TABLES. A greyed control still promises
                  a screen, and there isn't one. */}
              {canExpand(id) ? (
                <button
                  type="button"
                  className="an-card-export"
                  title={`Open ${spec.title.toLowerCase()} in full`}
                  aria-label={`Open ${spec.title.toLowerCase()} in full`}
                  onClick={() => setOpenCard(id)}
                >
                  <Maximize2 size={13} />
                </button>
              ) : null}
            </span>
          )}
        </div>
        {body}
        {CAVEATS[id] ? <p className="an-caveat">{CAVEATS[id]}</p> : null}
      </section>
    );
  };

  const topRows = (data: { payload: { value: string; sessions: number; share: number; bookings: number }[] } | null, label?: (value: string) => string) =>
    (data?.payload ?? []).map((row) => ({
      label: label ? label(row.value) : row.value,
      value: row.sessions,
      share: row.share,
      // ONE colour down a ranked list, not one per row. Every bar here measures
      // the same thing, so a per-row hue would be decoration — and worse, it
      // would be assigned by RANK, so a filter that reorders the list repaints
      // the survivors and the eye reads a change that did not happen. The
      // categorical palette is spent where the slices really are different
      // things: the channel donut.
      color: 'var(--viz-1)',
      meta: row.bookings ? `${row.bookings} reservation${row.bookings === 1 ? '' : 's'}` : undefined,
    }));

  // Plotted twice — once in the card, once at four times the height when it is
  // opened out — so the points are built once. Annotations are marked on the
  // chart AND readable in the tooltip: six months later a step change on this
  // line needs an explanation attached to it, not one somebody has to remember.
  const sessionSpark = (series.data ?? []).map((point) => point.value);
  const visitorSpark = (visitorSeries.data ?? []).map((point) => point.value);
  const pageviewSpark = (pageviewSeries.data ?? []).map((point) => point.value);
  const bookingSpark = (bookingSeries.data ?? []).map((point) => point.value);

  const trendPoints = (series.data ?? []).map((point) => ({
    label: shortDay(point.day),
    value: point.value,
    previous: point.previous,
    note: (notes.data ?? []).find((entry) => entry.day === point.day)?.label ?? null,
  }));

  /**
   * The acquisition card's body.
   *
   * Channels keeps the donut, because those slices are genuinely different
   * things and the ring is what says so at a glance. Referrers and campaigns
   * are one measure ranked, so they get the list alone — a ring over a ranked
   * list would be decoration.
   *
   * The ring's own legend is OFF here, which is allowed for exactly one reason:
   * the list beside it carries the same swatch, the label, the value and the
   * share, so identity still never rests on the colour.
   */
  const acquisitionBody = (() => {
    if (acqTab === 'referrers') {
      return <BarList rows={topRows(referrers.data)} emptyLabel="No referrers in this range." />;
    }
    if (acqTab === 'campaigns') {
      return (
        <BarList
          rows={(campaigns.data?.payload ?? []).map((row) => {
            // A row one of the CRM's own campaigns produced is named for the
            // campaign and opens it, where this mount can reach Campaigns; a
            // tag typed by hand into an ad stays the tag it is.
            const campaign = row.campaign ?? null;
            return {
              label: campaign ? campaign.name : row.value,
              value: row.sessions,
              share: row.share,
              color: 'var(--viz-1)',
              meta: row.bookingValue
                ? `${money(row.bookingValue)} from ${row.bookings} reservation${row.bookings === 1 ? '' : 's'}`
                : undefined,
              onSelect: campaign && onOpenCampaign ? () => onOpenCampaign(campaign.id) : undefined,
            };
          })}
          emptyLabel="No tagged campaigns in this range."
        />
      );
    }
    const rows = channels.data?.payload ?? [];
    const head = rows.slice(0, DONUT_SLOTS);
    const tail = rows.slice(DONUT_SLOTS);
    const slices = head.map((row, index) => ({
      label: channelLabel(row.value),
      value: row.sessions,
      color: seriesColor(index),
      bookings: row.bookings,
    }));
    // A sixth channel folds into Other rather than getting a hue that would be
    // indistinguishable from one of these in dark mode.
    if (tail.length) {
      slices.push({
        label: 'Other',
        value: tail.reduce((sum, row) => sum + row.sessions, 0),
        color: OTHER,
        bookings: tail.reduce((sum, row) => sum + row.bookings, 0),
      });
    }
    const total = slices.reduce((sum, row) => sum + row.value, 0);
    return (
      <div className="an-acq">
        <Donut size={168} legend={false} slices={slices} />
        <BarList
          swatches
          rows={slices.map((row) => ({
            label: row.label,
            value: row.value,
            share: total ? row.value / total : 0,
            color: row.color,
            meta: row.bookings ? `${row.bookings} reservation${row.bookings === 1 ? '' : 's'}` : undefined,
          }))}
          emptyLabel="No visits in this range."
        />
      </div>
    );
  })();

  const bodies: Record<string, React.ReactNode> = {
    trend: (
      <>
        <LineChart
          points={trendPoints}
          valueLabel="Sessions"
          compareLabel={compare === 'year' ? 'Same period last year' : 'Previous period'}
        />
        <Annotations
          api={api}
          siteId={site.id}
          rows={notes.data ?? []}
          days={(series.data ?? []).map((point) => point.day)}
          mayManage={mayManage}
          onChanged={notes.reload}
        />
      </>
    ),

    live: (
      <div>
        <div className="an-stat-value" style={{ fontSize: 32 }}>
          <span className={`an-live-dot${live?.active ? ' beating' : ''}`} style={{ background: live?.active ? 'var(--c-green)' : 'var(--c-muted)' }} />
          {live?.active ?? 0}
        </div>
        <div className="an-stat-foot">on the site in the last 5 minutes</div>
        {live?.known?.length ? (
          <p className="an-card-note" style={{ display: 'block', marginTop: 8 }}>
            Including {live.known.slice(0, 3).map((person) => person.name || 'a signed-in customer').join(', ')}
            {live.known.length > 3 ? ` and ${live.known.length - 3} more` : ''}.
          </p>
        ) : null}
        <div style={{ marginTop: 10 }}>
          <BarList
            rows={(live?.byPath ?? []).slice(0, 5).map((row) => ({
              label: row.path,
              value: row.active,
              share: live?.active ? row.active / live.active : 0,
              // Deliberately not the series colour every other card uses: this
              // is a gauge of right now, and the difference is what stops it
              // being read as another slice of the range above it.
              color: 'var(--viz-3)',
            }))}
            emptyLabel="Nobody on the site right now."
          />
        </div>
      </div>
    ),

    /*
     * The funnel and the three conversion figures, in one card.
     *
     * They were two cards a third of the grid each, which asked the reader to
     * hold "312 bookings" in their head while finding the step it came from.
     * One card, full width, puts the drop-offs and what survived them side by
     * side — and it is the only card here that is about money, so it gets the
     * band to itself.
     */
    bookings: (
      <div className="an-book">
        <BarList
          rows={(funnel.data ?? []).map((step) => ({
            label: step.label,
            value: step.sessions,
            share: step.share,
            color: 'var(--viz-1)',
            meta: step.dropOff ? `${exact(step.dropOff)} left here` : undefined,
          }))}
          emptyLabel="No funnel steps configured for this install."
        />
        {stats ? (
          <div className="an-book-side">
            {statTile('Attributed reservations', exact(stats.bookings), stats.bookings, previous?.bookings)}
            {statTile('Reservation value', money(stats.bookingValue), stats.bookingValue, previous?.bookingValue)}
            {statTile('Signed-in visits', percent(stats.identifiedRate), stats.identifiedRate, previous?.identifiedRate)}
          </div>
        ) : null}
      </div>
    ),

    acquisition: acquisitionBody,

    content:
      contentTab === 'pages' ? (
        <BarList rows={topRows(pages.data)} emptyLabel="No pages viewed in this range." />
      ) : (
        <BarList rows={topRows(entries.data)} emptyLabel="No landing pages in this range." />
      ),

    geo: (
      <div className="an-geo">
        <Suspense fallback={<div className="an-empty">Loading the map…</div>}>
          <WorldMap
            rows={countries.data?.payload ?? []}
            selected={geoCountry}
            onSelect={setGeoCountry}
          />
        </Suspense>
        <div>
          {geoCountry ? (
            <div className="an-geo-crumb">
              <button type="button" onClick={() => setGeoCountry(null)}>
                Everywhere
              </button>
              <span>›</span>
              <span>
                {flagOf(geoCountry)} {geoCountry}
              </span>
            </div>
          ) : null}
          <BarList
            rows={
              geoCountry
                ? (geoDrill.data?.payload ?? []).map((row) => ({
                    label: row.value,
                    value: row.sessions,
                    share: row.share,
                    color: 'var(--viz-1)',
                  }))
                : (countries.data?.payload ?? []).slice(0, 8).map((row) => ({
                    label: `${flagOf(row.value)} ${row.value}`.trim(),
                    value: row.sessions,
                    share: row.share,
                    color: 'var(--viz-1)',
                    onSelect: () => setGeoCountry(row.value),
                  }))
            }
            emptyLabel={geoCountry ? 'No towns or cities recorded here.' : 'No location data yet.'}
          />
        </div>
      </div>
    ),

    devices:
      deviceTab === 'devices' ? (
        <BarList rows={topRows(devices.data)} emptyLabel="No devices recorded in this range." />
      ) : (
        <BarList rows={topRows(browsers.data)} emptyLabel="No browsers recorded in this range." />
      ),

    hours: traffic.data ? (
      <Matrix cells={traffic.data.payload} max={traffic.data.stats.max} dowLabels={DOW_LABELS} />
    ) : null,

    sessions: (
      // Eight columns do not fit a one-column card on a narrow pane, and a
      // table wider than its card has to scroll INSIDE it — unwrapped it made
      // the whole dashboard scroll sideways, which moves every other card to
      // read one that did not fit.
      <div className="an-table-scroll">
        <table className="an-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Who</th>
              <th>Came from</th>
              <th>Path</th>
              <th className="an-num">Pages</th>
              <th className="an-num">Engaged</th>
              <th>Outcome</th>
              <th className="an-open" aria-label="Opens the visit" />
            </tr>
          </thead>
          <tbody>
            {(sessions.data?.payload ?? []).slice(0, 12).map((row) => (
              // Focusable, because opening a visit was mouse-only: the panel is
              // the whole point of this card and a keyboard could not reach it.
              <tr
                key={row.key}
                tabIndex={0}
                onClick={() => setOpenSession(row.key)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setOpenSession(row.key);
                }}
              >
                <td>{fmtDateTime(row.startedAt)}</td>
                <td>{row.contactName || (row.isIdentified ? 'Signed in' : 'Anonymous')}</td>
                <td>{channelLabel(row.channel)}</td>
                <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.entryPath}
                </td>
                <td className="an-num">{row.pageviews}</td>
                <td className="an-num">{duration(row.engagedSeconds)}</td>
                <td>{row.outcome === 'booking' ? 'Booked' : row.outcome === 'lead' ? 'Left a quote' : '—'}</td>
                <td className="an-open">
                  <ChevronRight size={14} />
                </td>
              </tr>
            ))}
            </tbody>
        </table>
      </div>
    ),
  };

  /**
   * What a card's expanded view shows ABOVE its tables — the drawing the third
   * of a grid could not give room to. Cards absent from here open straight onto
   * their tables; cards absent from CardDetail's own map do not open at all.
   */
  const figures: Record<string, React.ReactNode> = {
    trend: (
      <LineChart
        points={trendPoints}
        height={340}
        valueLabel="Sessions"
        compareLabel={compare === 'year' ? 'Same period last year' : 'Previous period'}
      />
    ),
    hours: traffic.data ? (
      <Matrix cells={traffic.data.payload} max={traffic.data.stats.max} dowLabels={DOW_LABELS} />
    ) : null,
    // The tab the reader has open, not all three: the panel opens out the card
    // they clicked the control on, and the tables underneath it follow the same
    // choice through `tables` below.
    acquisition: bodies.acquisition,
    // The drill is the SCREEN's state, not the panel's, so the map here and the
    // one on the card behind it agree on which country is picked — and the way
    // back out has to be in the panel too, or a country chosen on this map is a
    // dead end until it is closed.
    geo: (
      <>
        <Suspense fallback={<div className="an-empty">Loading the map…</div>}>
          <WorldMap rows={countries.data?.payload ?? []} selected={geoCountry} onSelect={setGeoCountry} />
        </Suspense>
        {geoCountry ? (
          <div className="an-geo-crumb">
            <button type="button" onClick={() => setGeoCountry(null)}>
              Everywhere
            </button>
            <span>›</span>
            <span>
              {flagOf(geoCountry)} {geoCountry}
            </span>
          </div>
        ) : null}
      </>
    ),
    // The card names three of them and counts the rest; opened out, everyone on
    // the site is named and every path they are on is listed.
    live: (
      <div>
        <div className="an-stat-value" style={{ fontSize: 38 }}>
          <span
            className={`an-live-dot${live?.active ? ' beating' : ''}`}
            style={{ background: live?.active ? 'var(--c-green)' : 'var(--c-muted)' }}
          />
          {live?.active ?? 0}
        </div>
        <div className="an-stat-foot">on the site in the last 5 minutes</div>
        {live?.known?.length ? (
          <p className="an-card-note" style={{ display: 'block', margin: '10px 0 0' }}>
            Signed in: {live.known.map((person) => person.name || 'a signed-in customer').join(', ')}.
          </p>
        ) : null}
        <div style={{ marginTop: 14 }}>
          <BarList
            rows={(live?.byPath ?? []).map((row) => ({
              label: row.path,
              value: row.active,
              share: live?.active ? row.active / live.active : 0,
              color: 'var(--viz-3)',
            }))}
            emptyLabel="Nobody on the site right now."
          />
        </div>
      </div>
    ),
  };

  const CAVEATS: Record<string, string> = {
    bookings:
      'A reservation is attributed when the visit can be tied to a signed-in customer, or when your confirmation page reports the reservation reference. Someone who books as a guest without either cannot be attributed, so this is a floor rather than a total.',
    live: 'Only customers who signed in on your own site are named. Everyone else is counted and nothing more.',
    geo: 'Pick a country to see the towns and cities inside it. That view is read from the visits themselves rather than from the daily totals, so unlike every other card here it only reaches back as far as your retention setting.',
  };

  /**
   * The two caveats the headline band carries, which are one sentence now.
   *
   * They used to be a card caveat apiece — one under the six figures and one
   * under a whole card that existed to show a single crawler count. Both say
   * the same kind of thing (what this number is NOT), the band is where both
   * apply, and crawler traffic being visible rather than silently dropped is
   * the reason the rest of the screen can be trusted.
   */
  const bandNote = stats
    ? `Visitors counts each person once across the whole range, so it is smaller than the daily figures added together. ${exact(stats.botSessions)} crawler visits (${exact(stats.botPageviews)} pageviews) were recorded and left out of every figure above.`
    : null;

  /**
   * What the expanded panel tabulates.
   *
   * A tabbed card opens out on the tab the reader had OPEN, not on all of its
   * dimensions — clicking expand on Referrers and landing on Channels is the
   * panel answering a question nobody asked. `undefined` falls through to
   * DETAIL_TABLES, which is still the whole answer for a card with one.
   */
  const detailTablesFor = (id: string): DetailTable[] | undefined => {
    if (id === 'geo' && geoCountry) return [{ dim: 'city', country: geoCountry }];
    if (id === 'acquisition') {
      if (acqTab === 'referrers') return [{ dim: 'referrer' }];
      if (acqTab === 'campaigns') return [{ dim: 'utm_campaign' }, { dim: 'utm_source' }, { dim: 'utm_medium' }];
      return [{ dim: 'channel', label: channelLabel }];
    }
    if (id === 'content') {
      return contentTab === 'pages' ? [{ dim: 'page' }, { dim: 'exit_page' }] : [{ dim: 'entry_page' }];
    }
    if (id === 'devices') {
      return deviceTab === 'devices' ? [{ dim: 'device' }, { dim: 'os' }] : [{ dim: 'browser' }];
    }
    return undefined;
  };

  /** The tabs a card draws in its head, and what they are switching. */
  const webOnly = site.platform === 'web';
  const tabsFor = (id: string): React.ReactNode => {
    if (id === 'acquisition') {
      return (
        <TabStrip
          label="Acquisition breakdown"
          tabs={ACQ_TABS.filter((tab) => webOnly || !tab.webOnly)}
          value={acqTab}
          onChange={(key) => setAcqTab(key as AcqTab)}
        />
      );
    }
    if (id === 'content') {
      return (
        <TabStrip
          label="Page breakdown"
          tabs={CONTENT_TABS.filter((tab) => webOnly || !tab.webOnly)}
          value={contentTab}
          onChange={(key) => setContentTab(key as ContentTab)}
        />
      );
    }
    if (id === 'devices') {
      return (
        <TabStrip
          label="Device breakdown"
          tabs={DEVICE_TABS}
          value={deviceTab}
          onChange={(key) => setDeviceTab(key as DeviceTab)}
        />
      );
    }
    return null;
  };

  /**
   * Whether the install is talking to us, as a chip rather than a sentence.
   *
   * It used to be spliced into the sub-line where the date range goes, so a
   * healthy install said "Website · 01/09/2026 to 28/09/2026" and a broken one
   * said "Website · nothing received in the last 24 hours" — the same slot
   * carrying either a fact or an alarm, with nothing to tell them apart at a
   * glance.
   */
  const health =
    site.health === 'never_reported'
      ? { tone: 'warn', label: 'No data yet' }
      : site.health === 'silent'
        ? { tone: 'warn', label: 'Quiet 24h' }
        : { tone: 'ok', label: 'Reporting' };

  return (
    <div className={embedded ? 'analytics embedded' : 'analytics'}>
      <div className="an-head">
        <div className="an-head-id">
          {switcher}
          <span className="an-title-mark" aria-hidden="true">
            <BarChart3 size={16} />
          </span>
          <div className="an-head-name">
            <div className="an-title-row">
              <h1 className="an-title">{site.name}</h1>
              <span className={`an-health ${health.tone}`}>
                <i aria-hidden="true" />
                {health.label}
              </span>
            </div>
            <p className="an-sub">
              {site.platform === 'app' ? 'Customer app' : 'Website'} ·{' '}
              {overview.data?.range
                ? `${overview.data.range.from} to ${overview.data.range.to}`
                : `${range.from} to ${range.to}`}
            </p>
          </div>
        </div>
        <div className="an-controls">
          {(sites.data?.length ?? 0) > 1 ? (
            <select
              className="select"
              aria-label="Install"
              value={site.id}
              onChange={(event) => setSiteId(Number(event.target.value))}
            >
              {(sites.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          ) : null}
          {site.platform === 'web' ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setHeatmapOpen(true)}
              title="Where people click and how far they scroll"
            >
              <MousePointerClick size={14} />
              Clicks
            </button>
          ) : null}
          {/* Five buttons across the top of a dashboard is a toolbar nobody
              reads. The two that open another screen entirely go behind one
              control; Clicks stays out because it is the only one an agent
              reaches for while reading the numbers. */}
          <div className="an-menu-wrap" ref={toolsRef}>
            <button
              type="button"
              className="btn btn-ghost"
              aria-haspopup="menu"
              aria-expanded={toolsOpen}
              onClick={() => setToolsOpen((was) => !was)}
            >
              Tools
              <ChevronDown size={13} />
            </button>
            {toolsOpen ? (
              <div className="an-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setBuilderOpen((was) => !was);
                    setToolsOpen(false);
                  }}
                >
                  <Link2 size={14} />
                  Link builder
                </button>
                {settingsAllowed ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      openSettings('auto', site.id);
                      setToolsOpen(false);
                    }}
                  >
                    <Settings2 size={14} />
                    Install and settings
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => setEditing((was) => !was)}>
            {editing ? <Check size={14} /> : <LayoutGrid size={14} />}
            {editing ? 'Done' : 'Edit layout'}
          </button>
          {editing ? (
            <button type="button" className="btn btn-ghost" onClick={() => persist([...DEFAULT_LAYOUT])}>
              <RotateCcw size={14} /> Reset
            </button>
          ) : null}
        </div>
      </div>

      {/* The filters travel with the reader, the title does not: the range and
          the comparison decide what every card below says, and a filter that
          has scrolled away is one an agent answers by scrolling back up rather
          than by changing. */}
      <div className="an-filters">
        <span className="an-filter-label">Range</span>
        <div className="an-seg" role="group" aria-label="Date range">
          {RANGES.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={rangeKey === entry.key ? 'on' : ''}
              onClick={() => setRangeKey(entry.key)}
            >
              {entry.label}
            </button>
          ))}
        </div>
        <span className="an-filter-rule" />
        <span className="an-filter-label">Compare</span>
        <div className="an-seg" role="group" aria-label="Compare with">
          <button type="button" className={compare === 'previous' ? 'on' : ''} onClick={() => setCompare('previous')}>
            vs previous
          </button>
          <button type="button" className={compare === 'year' ? 'on' : ''} onClick={() => setCompare('year')}>
            vs last year
          </button>
          <button type="button" className={compare === 'none' ? 'on' : ''} onClick={() => setCompare('none')}>
            off
          </button>
        </div>
        <span className="an-filter-space" />
        {busy ? <Spinner /> : null}
        {lastUpdated ? <span className="an-updated">Updated {fmtTime(lastUpdated)}</span> : null}
        <RefreshButton
          refreshing={refreshing}
          busy={overview.stale}
          onClick={refreshAll}
          className="btn btn-ghost btn-icon"
          title="Refresh every card"
          label={null}
        />
      </div>

      {site.health === 'never_reported' ? (
        <Note tone="warn">
          {site.platform === 'app'
            ? 'Nothing has reached us from the app yet. It starts reporting the next time someone opens it while this install is switched on, and until then every card below will be empty.'
            : 'Nothing has reached us from this install yet. Until the snippet is on the site, every card below will be empty.'}
          {settingsAllowed ? (
            <>
              {' '}
              <button type="button" className="an-link" onClick={() => openSettings('install', site.id)}>
                {site.platform === 'app' ? 'Check the install' : 'Get the code'}
              </button>
            </>
          ) : null}
        </Note>
      ) : null}
      {overview.error ? <ErrorNote error={overview.error} /> : null}

      {builderOpen ? <UtmBuilder onClose={() => setBuilderOpen(false)} /> : null}

      {/* `data-card` survives the headline leaving the grid: it is what the
          handbook's capture script photographs, and the band is still the same
          six figures under the same name. */}
      {stats ? (
        <div className="an-band" data-card="headline">
          <div className="an-band-tiles">
            {bandTile('Visitors', exact(stats.visitors), stats.visitors, previous?.visitors, visitorSpark)}
            {bandTile('Sessions', exact(stats.sessions), stats.sessions, previous?.sessions, sessionSpark)}
            {bandTile('Pageviews', exact(stats.pageviews), stats.pageviews, previous?.pageviews, pageviewSpark)}
            {bandTile(
              'Engaged time',
              duration(stats.avgEngagedSeconds),
              stats.avgEngagedSeconds,
              previous?.avgEngagedSeconds,
              [],
            )}
            {bandTile('Bounce rate', percent(stats.bounceRate), stats.bounceRate, previous?.bounceRate, [], false)}
            {bandTile('Reservations', exact(stats.bookings), stats.bookings, previous?.bookings, bookingSpark)}
          </div>
          {bandNote ? <p className="an-band-note">{bandNote}</p> : null}
        </div>
      ) : null}

      {groups.map((group) => (
        <div className="an-group" key={group.id}>
          <div className="an-section">
            <span className="an-section-label">{group.label}</span>
            <span className="an-section-rule" />
          </div>
          <div className="an-grid">
            {group.cards.map((entry) => card(entry.id, bodies[entry.id] ?? null, tabsFor(entry.id)))}
          </div>
        </div>
      ))}

      {editing && hiddenCards.length ? (
        <div className="an-hidden-list">
          <span className="an-card-note">Hidden:</span>
          {hiddenCards.map((entry) => (
            <button key={entry.id} type="button" className="btn btn-ghost" onClick={() => persist(toggleHidden(cards, entry.id))}>
              + {specOf(entry.id)?.title ?? entry.id}
            </button>
          ))}
        </div>
      ) : null}

      {openCard ? (
        <CardDetail
          api={api}
          range={range}
          cardId={openCard}
          title={specOf(openCard)?.title ?? openCard}
          siteName={site.name}
          rangeLabel={
            overview.data?.range
              ? `${overview.data.range.from} to ${overview.data.range.to}`
              : `${range.from} to ${range.to}`
          }
          tables={detailTablesFor(openCard)}
          covered={openSession !== null}
          onOpenSession={setOpenSession}
          onClose={() => setOpenCard(null)}
        >
          {figures[openCard] ?? null}
        </CardDetail>
      ) : null}

      {openSession ? (
        <SessionDrawer api={api} sessionKey={openSession} siteId={site.id} onClose={() => setOpenSession(null)} />
      ) : null}
    </div>
  );
}

export { CARD_CATALOGUE };

/**
 * Notes pinned to a day, drawn as flags on every time series.
 *
 * Reading requires `view` and writing requires `manage`, because a note is
 * shared: everyone looking at this chart reads the same sentence, and the wrong
 * one is worse than none.
 */
function Annotations({
  api,
  siteId,
  rows,
  days,
  mayManage,
  onChanged,
}: {
  api: AnalyticsApi;
  siteId: number;
  rows: { id: number; day: string; label: string }[];
  days: string[];
  mayManage: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [day, setDay] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const chosen = day || days[days.length - 1] || '';

  const add = async () => {
    if (!label.trim() || !chosen) return;
    setBusy(true);
    try {
      await api.createAnalyticsAnnotation(siteId, chosen, label.trim());
      setLabel('');
      setAdding(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    setBusy(true);
    try {
      await api.deleteAnalyticsAnnotation(id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (!rows.length && !mayManage) return null;

  return (
    <>
      {rows.length ? (
        <div className="an-notes">
          {rows.map((row) => (
            <span className="an-note-chip" key={row.id}>
              <b>{shortDay(row.day)}</b>
              {row.label}
              {mayManage ? (
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`Remove the note on ${shortDay(row.day)}`}
                  onClick={() => void remove(row.id)}
                >
                  <X size={11} />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      {mayManage ? (
        adding ? (
          <div className="an-note-add">
            <select className="select" value={chosen} onChange={(event) => setDay(event.target.value)}>
              {days.map((value) => (
                <option key={value} value={value}>
                  {shortDay(value)}
                </option>
              ))}
            </select>
            <input
              className="input"
              value={label}
              placeholder="New homepage went live"
              maxLength={120}
              onChange={(event) => setLabel(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && void add()}
            />
            <button className="btn btn-primary" disabled={busy || !label.trim()} onClick={() => void add()}>
              Add
            </button>
            <button className="btn btn-ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="an-note-add">
            <button className="btn btn-ghost" onClick={() => setAdding(true)}>
              + Mark a day
            </button>
          </div>
        )
      ) : null}
    </>
  );
}
